import React, { useEffect, useState } from "react";
import Shell, { StatCard, Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import { LeadsBoard } from "@/pages/marketing/MarketingDashboard";
import { AlertsBoard } from "@/components/NextBestAction";
import ActionCenter from "@/components/ActionCenter";
import ConstructorHub from "@/pages/constructor/ConstructorHub";
import { ConstructionProgressPicker } from "@/components/construction/ConstructionProgressPublisher";
import { useFocusRow } from "@/hooks/useFocusRow";
import {
  ChartBar, Camera, Receipt, ListChecks, Sparkle, Plus, Trash, UploadSimple, ArrowClockwise, Bell,
  CaretDown, CaretRight, Play, Check as CheckIcon, ArrowCounterClockwise, SquaresFour, Rows,
  Lightning,
} from "@phosphor-icons/react";
import {
  TASK_STATUSES, PRIORITY_BADGE_CLASS, priorityLabel, statusLabel,
} from "@/lib/taskStatuses";

// AI failure markers — if any of these appear in the backend `detail` we hide
// the raw provider message and show a human copy + apply a local sort fallback.
const AI_UNAVAILABLE_MARKERS = [
  "budget has been exceeded",
  "litellm",
  "openaiexception",
  "badrequesterror",
  "rate limit",
  "quota",
  "insufficient_quota",
];

const PRIO_RANK = { alta: 0, media: 1, baja: 2 };

// Client-side fallback ordering when the AI organizer is unavailable.
// Sorts by due_date asc (missing dates last), then priority alta→media→baja.
// Pure — does not persist anything.
function localSortTasks(list) {
  return [...list].sort((a, b) => {
    const da = a.due_date || "";
    const db_ = b.due_date || "";
    if (da && db_) {
      if (da !== db_) return da < db_ ? -1 : 1;
    } else if (da) {
      return -1;
    } else if (db_) {
      return 1;
    }
    const pa = PRIO_RANK[a.priority || "media"] ?? 1;
    const pb = PRIO_RANK[b.priority || "media"] ?? 1;
    return pa - pb;
  });
}

const TABS = [
  { key: "overview", label: "Operations", icon: ChartBar },
  { key: "action-center", label: "Action Center", icon: Lightning },
  { key: "alerts", label: "Alerts", icon: Bell },
  { key: "progress", label: "Log progress", icon: Camera },
  { key: "invoices", label: "Invoices (AI OCR)", icon: Receipt },
  { key: "tasks", label: "Tasks (AI)", icon: ListChecks },
];
const ACCENT = "#9C5236";

// Tab keys accepted by the dv:constructor-tab CustomEvent. Used by the
// mobile bottom nav "Avance" item to jump into the publish flow.
const CONSTRUCTOR_TAB_KEYS = new Set(["overview", "action-center", "alerts", "progress", "invoices", "tasks"]);

export default function ConstructorDashboard() {
  const [tab, setTab] = useState("overview");

  // Listen for mobile bottom-nav requests (mirrors investor pattern).
  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail;
      if (typeof next === "string" && CONSTRUCTOR_TAB_KEYS.has(next)) {
        setTab(next);
      }
    };
    window.addEventListener("dv:constructor-tab", handler);
    return () => window.removeEventListener("dv:constructor-tab", handler);
  }, []);

  return (
    <Shell tabs={TABS} activeTab={tab} onTabChange={setTab} lang="en">
      {tab === "overview" && <ConstructorHub onNavigate={setTab} />}
      {tab === "action-center" && <ActionCenter lang="en" />}
      {tab === "alerts" && <AlertsBoard onNavigate={setTab} lang="en" />}
      {tab === "progress" && <ConstructionProgressPicker accent={ACCENT} />}
      {tab === "invoices" && <InvoiceOCR />}
      {tab === "tasks" && <TasksAI lang="en" />}
    </Shell>
  );
}

// ProgressUpload + ProgressUpdateForm extracted to
// components/construction/ConstructionProgressPublisher.jsx so admin and
// constructor share the exact same flow (drag&drop, multi-upload, preview,
// remove, category, 8 MB client guard, project-wide branch).


const INVOICE_CATEGORY_OPTIONS = [
  { value: "materials", label: "Materiales" },
  { value: "labor", label: "Mano de obra" },
  { value: "permits", label: "Permisos" },
  { value: "utilities", label: "Suministros" },
  { value: "services", label: "Servicios" },
  { value: "logistics", label: "Logística" },
  { value: "furnishing", label: "Mobiliario / FF&E" },
  { value: "other", label: "Otros" },
];

function InvoiceOCR() {
  const [invoices, setInvoices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [form, setForm] = useState({ provider: "", date: "", concept: "", amount: "", currency: "EUR", category: "" });
  const [msg, setMsg] = useState("");

  const loadInvoices = () => api.get("/invoices").then(r => setInvoices(r.data));
  useEffect(() => { loadInvoices(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setMsg(""); setExtracted(null);
    const b64 = await new Promise(res => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",")[1]);
      r.readAsDataURL(file);
    });
    setPreview(b64);
    try {
      const { data } = await api.post("/ai/ocr-invoice", { image_base64: b64, mime_type: file.type });
      setExtracted(data);
      setForm({
        provider: data.provider || "",
        date: data.date || "",
        concept: data.concept || "",
        amount: data.amount ?? "",
        currency: data.currency || "EUR",
        category: "",   // F-25: admin must choose explicitly
      });
    } catch (e) { setMsg(formatError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!form.category) {
      setMsg("Selecciona una categoría antes de guardar.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/invoices", { ...form, amount: Number(form.amount) || null, image_base64: preview });
      setMsg("✓ Invoice saved.");
      setPreview(null); setExtracted(null);
      setForm({ provider: "", date: "", concept: "", amount: "", currency: "EUR", category: "" });
      loadInvoices();
    } catch (e) { setMsg(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Section title="New invoice with smart OCR" subtitle="Upload a photo and Claude will extract the data automatically.">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="dv-card">
            <label className="dv-btn dv-btn-outline cursor-pointer w-full">
              <UploadSimple size={18} /> {busy ? "Analyzing with AI…" : "Upload invoice photo"}
              <input type="file" accept="image/*" hidden onChange={onUpload} data-testid="invoice-upload" />
            </label>
            {preview && <img src={`data:image/jpeg;base64,${preview}`} alt="" className="mt-4 rounded-md max-h-80 object-contain mx-auto" />}
          </div>
          <div className="dv-card">
            {extracted ? (
              <div className="space-y-3">
                <div className="text-xs tracking-widest uppercase text-stone-500 flex items-center gap-2">
                  <Sparkle size={14} weight="fill" /> Extracted by AI (editable)
                </div>
                <Input label="Provider" v={form.provider} onChange={v => setForm({ ...form, provider: v })} tid="inv-provider" />
                <Input label="Date" v={form.date} onChange={v => setForm({ ...form, date: v })} tid="inv-date" />
                <Input label="Concept" v={form.concept} onChange={v => setForm({ ...form, concept: v })} tid="inv-concept" />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Amount" v={form.amount} onChange={v => setForm({ ...form, amount: v })} tid="inv-amount" />
                  <Input label="Currency" v={form.currency} onChange={v => setForm({ ...form, currency: v })} tid="inv-currency" />
                </div>
                <div>
                  <label className="text-xs text-stone-500">Categoría <span className="text-red-600">*</span></label>
                  <select
                    className="dv-input"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    required
                    data-testid="inv-category"
                  >
                    <option value="" disabled>-- Selecciona categoría --</option>
                    {INVOICE_CATEGORY_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <button onClick={save} disabled={busy || !form.category} className="dv-btn w-full text-white" style={{ background: ACCENT }} data-testid="invoice-save">
                  Save invoice
                </button>
                {msg && <div className="text-sm text-stone-600">{msg}</div>}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-stone-400 text-sm text-center p-8">
                Upload a photo and the AI will extract provider, date, amount and concept automatically.
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section title={`Registered invoices (${invoices.length})`}>
        <div className="dv-card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs tracking-wider uppercase text-stone-500">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Provider</th>
                <th className="px-4 py-3">Concept</th><th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-stone-400">No invoices yet.</td></tr>
              ) : invoices.map(i => (
                <tr key={i.invoice_id} className="border-b border-stone-100">
                  <td className="px-4 py-3">{i.date}</td>
                  <td className="px-4 py-3 font-medium">{i.provider}</td>
                  <td className="px-4 py-3 text-stone-600">{i.concept}</td>
                  <td className="px-4 py-3 text-right font-serif-dv text-lg">{Number(i.amount || 0).toLocaleString()} {i.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

function Input({ label, v, onChange, tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-stone-500">{label}</label>
      <input className="dv-input mt-1" value={v ?? ""} onChange={e => onChange(e.target.value)} data-testid={tid} />
    </div>
  );
}

export function TasksAI({ lang = "es" }) {
  const T = lang === "en" ? {
    title: "Tasks with AI",
    subtitle: "Add tasks freely and let Claude prioritize and categorize them.",
    organize: "Organize with AI",
    analyzing: "Analyzing…",
    titlePlaceholder: "Task title",
    descPlaceholder: "Description (optional)",
    add: "Add",
    organizedOk: "✓ Tasks organized by AI.",
    aiUnavailable: "AI organization is unavailable right now. You can keep managing tasks manually.",
    aiGeneric: "Could not organize tasks right now.",
    priorityLbl: { alta: "High priority", media: "Medium priority", baja: "Low priority" },
    completedHdr: "Completed",
    showCompleted: "Show",
    hideCompleted: "Hide",
    viewKanban: "Kanban",
    viewPriority: "Priorities",
    actStart: "Start",
    actComplete: "Complete",
    actReopen: "Reopen",
    actBack: "Back to new",
    inProgressBadge: "In progress",
  } : {
    title: "Tareas con IA",
    subtitle: "Añade tareas libremente y deja que Claude las priorice y categorice.",
    organize: "Organizar con IA",
    analyzing: "Analizando…",
    titlePlaceholder: "Título de la tarea",
    descPlaceholder: "Descripción (opcional)",
    add: "Añadir",
    organizedOk: "✓ Tareas organizadas por la IA.",
    aiUnavailable: "La organización con IA no está disponible ahora. Puedes seguir gestionando tareas manualmente.",
    aiGeneric: "No se pudo organizar ahora mismo.",
    priorityLbl: { alta: "Prioridad alta", media: "Prioridad media", baja: "Prioridad baja" },
    completedHdr: "Completadas",
    showCompleted: "Ver",
    hideCompleted: "Ocultar",
    viewKanban: "Kanban",
    viewPriority: "Prioridades",
    actStart: "Iniciar",
    actComplete: "Completar",
    actReopen: "Reabrir",
    actBack: "Volver a nuevas",
    inProgressBadge: "En curso",
  };

  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showDone, setShowDone] = useState(false);
  // View toggle persists per browser. Default "priority" keeps current users
  // on the familiar layout; "kanban" is opt-in while we evaluate it.
  const [view, setView] = useState(() => {
    try {
      const v = localStorage.getItem("dv:tasks-view");
      return v === "kanban" || v === "priority" ? v : "priority";
    } catch { return "priority"; }
  });
  useEffect(() => {
    try { localStorage.setItem("dv:tasks-view", view); } catch { /* ignore */ }
  }, [view]);

  const load = () => api.get("/tasks").then(r => setTasks(r.data));
  useEffect(() => { load(); }, []);
  useFocusRow([tasks]);

  const addTask = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { title, description: desc });
      setTitle(""); setDesc(""); load();
    } catch (e) { alert(formatError(e)); }
  };

  const organizeAll = async () => {
    if (tasks.length === 0) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/ai/organize-tasks", {
        tasks: tasks.map(t => ({ title: t.title, description: t.description })),
        role: "constructor",
      });
      await Promise.all(tasks.map((t, i) => {
        const ai = data.tasks[i];
        if (!ai) return null;
        return api.patch(`/tasks/${t.task_id}`, {
          priority: ai.priority,
          category: ai.category,
        });
      }));
      setMsg(T.organizedOk);
      load();
    } catch (e) {
      const detail = String(e?.response?.data?.detail || e?.message || "").toLowerCase();
      const isAiOutage = AI_UNAVAILABLE_MARKERS.some(m => detail.includes(m));
      if (isAiOutage) {
        setMsg(T.aiUnavailable);
        // Local fallback: reorder current list by due_date asc then priority.
        // Not persisted — purely visual until next /tasks reload.
        setTasks(prev => localSortTasks(prev));
      } else {
        setMsg(T.aiGeneric);
      }
    }
    finally { setBusy(false); }
  };

  const toggleStatus = async (t) => {
    const next = t.status === "done" ? "todo" : "done";
    await api.patch(`/tasks/${t.task_id}`, { status: next });
    load();
  };

  // Explicit status transition — used by the Kanban quick-action buttons.
  const setStatus = async (t, nextStatus) => {
    if (t.status === nextStatus) return;
    await api.patch(`/tasks/${t.task_id}`, { status: nextStatus });
    load();
  };

  const del = async (t) => {
    const ok = window.confirm(
      `¿Eliminar la tarea "${t.title}"? Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    await api.delete(`/tasks/${t.task_id}`);
    load();
  };

  return (
    <Section title={T.title} subtitle={T.subtitle} action={
      <button onClick={organizeAll} disabled={busy || tasks.length === 0} className="dv-btn dv-btn-outline" data-testid="ai-organize">
        <Sparkle size={16} weight="fill" /> {busy ? T.analyzing : T.organize}
      </button>
    }>
      <form onSubmit={addTask} className="dv-card mb-6 flex gap-3 flex-wrap">
        <input className="dv-input flex-1 min-w-[200px]" placeholder={T.titlePlaceholder} value={title} onChange={e => setTitle(e.target.value)} required data-testid="task-title" />
        <input className="dv-input flex-1 min-w-[200px]" placeholder={T.descPlaceholder} value={desc} onChange={e => setDesc(e.target.value)} data-testid="task-desc" />
        <button className="dv-btn dv-btn-primary"><Plus size={16} /> {T.add}</button>
      </form>
      {msg && <div className="mb-4 text-sm text-stone-600">{msg}</div>}

      {/* View toggle — Kanban vs Priorities. Default Priorities; persisted in
          localStorage so the choice survives reloads. */}
      <div className="mb-4 flex justify-end" data-testid="tasks-view-toggle">
        <div className="inline-flex rounded-md border border-stone-200 bg-white overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setView("kanban")}
            data-testid="tasks-view-kanban"
            className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"}`}
          >
            <SquaresFour size={12} /> {T.viewKanban}
          </button>
          <button
            type="button"
            onClick={() => setView("priority")}
            data-testid="tasks-view-priority"
            className={`px-3 py-1.5 inline-flex items-center gap-1.5 border-l border-stone-200 ${view === "priority" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"}`}
          >
            <Rows size={12} /> {T.viewPriority}
          </button>
        </div>
      </div>

      {view === "kanban" && (
        <KanbanView tasks={tasks} T={T} lang={lang} setStatus={setStatus} del={del} />
      )}

      {view === "priority" && (
      <>
      <div className="grid md:grid-cols-3 gap-4">
        {["alta", "media", "baja"].map(prio => (
          <div key={prio}>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${prio === "alta" ? "bg-red-500" : prio === "media" ? "bg-amber-500" : "bg-emerald-500"}`} />
              {T.priorityLbl[prio]} ({tasks.filter(t => t.status !== "done" && (t.priority || "media") === prio).length})
            </div>
            <div className="space-y-3">
              {tasks.filter(t => t.status !== "done" && (t.priority || "media") === prio).map(t => {
                const isAuto = typeof t.created_by === "string" && t.created_by.startsWith("automation:");
                return (
                <div key={t.task_id} className={`dv-card !p-4 ${t.status === "done" ? "opacity-60" : ""}`} data-testid={`task-${t.task_id}`} data-focus-id={t.task_id}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={t.status === "done"} onChange={() => toggleStatus(t)} className="mt-1.5" />
                    <div className="flex-1">
                      <div className={`font-medium ${t.status === "done" ? "line-through" : ""}`}>
                        {isAuto && (
                          <span
                            className="mr-1.5 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-800 border border-cyan-200 align-middle"
                            title={`Creada automáticamente · ${t.created_by}`}
                            data-testid={`task-auto-${t.task_id}`}
                          >
                            auto
                          </span>
                        )}
                        {t.title}
                      </div>
                      {t.description && <div className="text-xs text-stone-500 mt-1">{t.description}</div>}
                      {(t.category || t.due_date) && (
                        <div className="text-xs mt-2 flex flex-wrap gap-1.5 items-center">
                          {t.category && <span className="dv-badge">{t.category}</span>}
                          {t.due_date && <span className="text-stone-500">vence {t.due_date}</span>}
                        </div>
                      )}
                    </div>
                    <button onClick={() => del(t)} className="text-stone-300 hover:text-red-500"><Trash size={16} /></button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {(() => {
        const done = tasks
          .filter(t => t.status === "done")
          .sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
        if (done.length === 0) return null;
        return (
          <div className="mt-8" data-testid="tasks-completed-section">
            <button
              type="button"
              onClick={() => setShowDone(v => !v)}
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 hover:text-stone-900"
              data-testid="tasks-completed-toggle"
            >
              {showDone ? <CaretDown size={12} /> : <CaretRight size={12} />}
              {T.completedHdr} ({done.length})
              <span className="text-[10px] text-stone-400 normal-case tracking-normal">
                · {showDone ? T.hideCompleted : T.showCompleted}
              </span>
            </button>
            {showDone && (
              <div className="mt-3 space-y-2">
                {done.map(t => (
                  <div
                    key={t.task_id}
                    className="dv-card !p-4 opacity-60"
                    data-testid={`task-done-${t.task_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggleStatus(t)}
                        className="mt-1.5"
                      />
                      <div className="flex-1">
                        <div className="font-medium line-through">{t.title}</div>
                        {t.description && (
                          <div className="text-xs text-stone-500 mt-1 line-through">{t.description}</div>
                        )}
                        {(t.category || t.due_date) && (
                          <div className="text-xs mt-2 flex flex-wrap gap-1.5 items-center">
                            {t.category && <span className="dv-badge">{t.category}</span>}
                            {t.due_date && <span className="text-stone-500">vence {t.due_date}</span>}
                          </div>
                        )}
                      </div>
                      <button onClick={() => del(t)} className="text-stone-300 hover:text-red-500"><Trash size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
      </>
      )}
    </Section>
  );
}

/* ----------------------------- Kanban view ----------------------------- */
function KanbanView({ tasks, T, lang, setStatus, del }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="tasks-kanban">
      {TASK_STATUSES.map(col => {
        const list = tasks.filter(t => (t.status || "todo") === col.key);
        return (
          <div key={col.key} data-testid={`tasks-kanban-col-${col.key}`}>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${
                col.tone === "stone" ? "bg-stone-400" :
                col.tone === "amber" ? "bg-amber-500" : "bg-emerald-500"
              }`} />
              {statusLabel(col.key, lang)} ({list.length})
            </div>
            <div className="space-y-3">
              {list.map(t => (
                <KanbanCard
                  key={t.task_id}
                  task={t}
                  T={T}
                  lang={lang}
                  setStatus={setStatus}
                  del={del}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({ task: t, T, lang, setStatus, del }) {
  const isAuto = typeof t.created_by === "string" && t.created_by.startsWith("automation:");
  const prio = t.priority || "media";
  const isDone = t.status === "done";
  const isProgress = t.status === "in_progress";
  return (
    <div
      className={`dv-card !p-4 ${isDone ? "opacity-60" : ""}`}
      data-testid={`kanban-task-${t.task_id}`}
      data-focus-id={t.task_id}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {/* Priority badge + in-progress hint */}
          <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
            <span
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${PRIORITY_BADGE_CLASS[prio] || PRIORITY_BADGE_CLASS.media}`}
              data-testid={`kanban-task-prio-${t.task_id}`}
            >
              {priorityLabel(prio, lang)}
            </span>
            {isProgress && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                {T.inProgressBadge}
              </span>
            )}
            {isAuto && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-800 border border-cyan-200"
                title={`Creada automáticamente · ${t.created_by}`}
                data-testid={`kanban-task-auto-${t.task_id}`}
              >
                auto
              </span>
            )}
            {t.lead_id && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200"
                title={`Vinculada al lead ${t.lead_id}`}
              >
                lead
              </span>
            )}
          </div>
          <div className={`font-medium ${isDone ? "line-through" : ""}`}>{t.title}</div>
          {t.description && (
            <div className={`text-xs text-stone-500 mt-1 ${isDone ? "line-through" : ""}`}>
              {t.description}
            </div>
          )}
          {(t.category || t.due_date) && (
            <div className="text-xs mt-2 flex flex-wrap gap-1.5 items-center">
              {t.category && <span className="dv-badge">{t.category}</span>}
              {t.due_date && <span className="text-stone-500">vence {t.due_date}</span>}
            </div>
          )}
        </div>
        <button onClick={() => del(t)} className="text-stone-300 hover:text-red-500 shrink-0">
          <Trash size={16} />
        </button>
      </div>

      {/* Quick actions — vary by column */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {t.status === "todo" && (
          <button
            type="button"
            onClick={() => setStatus(t, "in_progress")}
            className="text-[11px] px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1"
            data-testid={`kanban-act-start-${t.task_id}`}
          >
            <Play size={11} weight="fill" /> {T.actStart}
          </button>
        )}
        {t.status === "in_progress" && (
          <>
            <button
              type="button"
              onClick={() => setStatus(t, "done")}
              className="text-[11px] px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1"
              data-testid={`kanban-act-complete-${t.task_id}`}
            >
              <CheckIcon size={11} weight="bold" /> {T.actComplete}
            </button>
            <button
              type="button"
              onClick={() => setStatus(t, "todo")}
              className="text-[11px] px-2 py-1 rounded border border-stone-200 text-stone-600 hover:bg-stone-50 inline-flex items-center gap-1"
              data-testid={`kanban-act-back-${t.task_id}`}
            >
              <ArrowCounterClockwise size={11} /> {T.actBack}
            </button>
          </>
        )}
        {t.status === "done" && (
          <button
            type="button"
            onClick={() => setStatus(t, "todo")}
            className="text-[11px] px-2 py-1 rounded border border-stone-200 text-stone-600 hover:bg-stone-50 inline-flex items-center gap-1"
            data-testid={`kanban-act-reopen-${t.task_id}`}
          >
            <ArrowCounterClockwise size={11} /> {T.actReopen}
          </button>
        )}
      </div>
    </div>
  );
}
